"use client";

import CommenDialog from "../tbs-sender/components/dialogbox";
import PreviewStep from "../tbs-sender/components/PreviewStep";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../../../components/loader";
import { APIGetTBSDetails } from "../../../service/api-service";
import { checkActionAuthorization } from "../../../service/shared.service";
import { GetTBSDetailsResEntity } from "../../../interface/tbs-tracker/send-tbs";
import { toastOptObj } from "../../../utils/constants";

// the TBS has been received at store and still has something that can go back
const RETURNABLE_STATUSES: string[] = ["received", "semi-issued", "issued"];

/** "Return Extra Items": the receiver's page again, locked except the items to untick, the remarks and the photos of what goes back. */
export default function ReturnExtraItems() {
    const navigate = useNavigate();
    const tbsId = useParams().id;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [tbsData, setTbsData] = useState<GetTBSDetailsResEntity | null>(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
    const [tbsNumber, setTbsNumber] = useState<string>("");

    const handleBack = () => {
        navigate(-1);
    };

    const handleSubmit = (submittedTbsNumber: string) => {
        setTbsNumber(submittedTbsNumber);
        setShowSuccessDialog(true);
    };

    const fetchTBSDetails = async () => {
        if (!tbsId) return;

        setIsLoading(true);

        try {
            const resp = await APIGetTBSDetails(tbsId);
            if (resp && resp.data && resp.status === 200) {
                const apiResp = resp.data;

                if (apiResp && apiResp.statusCode === 200 && apiResp.data) {
                    if (!RETURNABLE_STATUSES.includes(String(apiResp.data.status))) {
                        toast.error("Items can be returned only after the TBS is received at store.", { ...toastOptObj });
                        navigate(-1);
                        return;
                    }
                    setTbsData(apiResp.data);
                } else {
                    toast.error(apiResp.message || apiResp.errorMsg, { ...toastOptObj });
                    navigate(-1);
                }
            } else {
                toast.error(resp?.data?.message || "Unable to fetch TBS details.", { ...toastOptObj });
                navigate(-1);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!tbsId) {
            navigate(-1);
            return;
        }

        // the same permission that shows the Return Extra Items icon on the TBS Store list
        if (!checkActionAuthorization("upgrade", "Allow User To Return Extra Items")) {
            navigate("/forbidden", { replace: true });
            return;
        }

        fetchTBSDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tbsId]);

    if (isLoading || !tbsData) {
        return <Loader isLoading={true} />;
    }

    return (
        <>
            <div className="content-wrapper">
                <PreviewStep
                    previewData={tbsData}
                    onBack={handleBack}
                    onSubmit={handleSubmit}
                    isReturnExtra={true}
                />
            </div>
            <CommenDialog
                isOpen={showSuccessDialog}
                title="Items returned Successfully!"
                description={
                    <>
                        The selected items of tracker <strong>#{tbsNumber}</strong> have been returned.
                    </>
                }
                buttonText="Close"
                onClose={() => {
                    setShowSuccessDialog(false);
                    navigate(-1);
                }}
            />
        </>
    );
}
